import { safeDivide } from "@/utilities/number";

/**
 * In mobile app, dataset cartesian and plan positioning are
 * displayed in a 16/9 box. If the PDF/IMG ratio is different,
 * white blank spacings are displayed around the PDF/IMG to fit
 * inside 16/9 ratio. The clickable zone to add/edit a marker is
 * taking the entire 16/9 box width and height. The dataset marker
 * relative coordinates saved in DB therefore are "wrong" and need
 * to be converted to work with Webapp ui which is not using the
 * 16/9 ratio box.
 */
export const MOBILE_APP_ZONE_RATIO = 16 / 9;

type Size = {
  width: number;
  height: number;
};

type Coordinates = {
  x: number;
  y: number;
};

export const convertRenderCoordinatesToDatasetCoordinates = (
  elementSize: Size, // PDF (plan positioning) or IMG (cartesian)
  clickCoordinates: Coordinates // from Webapp ui
): Coordinates => {
  const { x, y } = clickCoordinates;
  const { width, height } = elementSize;
  const elementRatio = width / height;

  // When the element ratio is the same ratio than the one used in mobile
  // app, it means dataset coordinates saved in database are exacts.
  // => No conversion needed.
  if (elementRatio === MOBILE_APP_ZONE_RATIO) {
    return {
      x: x / width,
      y: y / height,
    };
  }

  // When the element ratio is smaller, it means that the mobile
  // app rendering adds white clickable spaces on the right and the
  // left of the element. The dataset coordinates saved in the DB
  // therefore have an incorrect `x`.
  // => Let's convert `x` coordinate to ADD space on left side
  if (elementRatio < MOBILE_APP_ZONE_RATIO) {
    const fullWidthRef = height * MOBILE_APP_ZONE_RATIO;
    const leftBlankSpace = (fullWidthRef - width) / 2;
    const convertedX = x + leftBlankSpace;

    return {
      x: safeDivide(convertedX, fullWidthRef),
      y: safeDivide(y, height),
    };
  }

  // When the element ratio is bigger, it means that the mobile
  // app rendering adds white clickable spaces on the top and the
  // bottom of the element. The dataset coordinates saved in the DB
  // therefore have an incorrect `y`.
  // => Let's convert `y` coordinate to ADD space on top side
  const fullHeightRef = safeDivide(width, MOBILE_APP_ZONE_RATIO);
  const topBlankSpace = (fullHeightRef - height) / 2;
  const convertedY = y + topBlankSpace;

  return {
    x: safeDivide(x, width),
    y: safeDivide(convertedY, fullHeightRef),
  };
};

export const convertDatasetCoordinatesToRenderPosition = (
  elementSize: Size, // PDF (plan positioning) or IMG (cartesian)
  relativeCoordinates: Coordinates // from DB
): Coordinates => {
  const { x, y } = relativeCoordinates;
  const { width, height } = elementSize;
  const elementRatio = width / height;

  // When the element ratio is the same ratio than the one used in mobile
  // app, it means dataset coordinates saved in database are exacts.
  // => No conversion needed.
  if (elementRatio === MOBILE_APP_ZONE_RATIO) {
    return {
      x: x * width,
      y: y * height,
    };
  }

  // When the element ratio is smaller, it means that the mobile
  // app rendering adds white clickable spaces on the right and the
  // left of the element. The dataset coordinates saved in the DB
  // therefore have an incorrect `x`.
  // => Let's convert `x` coordinate to REMOVE space on left side
  if (elementRatio < MOBILE_APP_ZONE_RATIO) {
    // const clickableSpaceHeightPx = imageHeightPx;
    const fullWidthRef = height * MOBILE_APP_ZONE_RATIO;
    const leftBlankSpace = (fullWidthRef - width) / 2;

    return {
      x: x * fullWidthRef - leftBlankSpace, // here `x` might be negative if the marker is outside the image
      y: y * height,
    };
  }

  // When the element ratio is bigger, it means that the mobile
  // app rendering adds white clickable spaces on the top and the
  // bottom of the element. The dataset coordinates saved in the DB
  // therefore have an incorrect `y`.
  // => Let's convert `y` coordinate to REMOVE space on top side
  const fullHeightRef = width / MOBILE_APP_ZONE_RATIO;
  const topBlankSpace = (fullHeightRef - height) / 2;

  return {
    x: x * width,
    y: y * fullHeightRef - topBlankSpace, // here `y` might be negative if the marker is outside the image
  };
};
